"use client"
import {
  useMemo,
  type ComponentType,
  type CSSProperties,
} from "react"
import { useAnimationTuning } from "@/app/playbook/animation/animationTuningContext"
import { resolveCssEasing } from "@/app/playbook/animation/animationTuningDefaults"
import {
  AmericaLogo,
  ChinaLogo,
  WorldLogo,
  type IconProps,
} from "@/ui/icons"
import {
  type PowerLabId,
  type PowerRegionId,
} from "../../../../power-bubble-chart/powerBubbleChartData"
import {
  genericFrontierLabIcon,
  labIconById,
} from "../../../../power-bubble-chart/powerChartIcons"
import {
  BLOCK_BORDER_RADIUS_PX,
  BLOCK_DIMMED_OPACITY,
  BLOCK_GAP_PX,
  BLOCK_HOVERED_CELL_OPACITY,
  LOGO_MIN_CELL_SIZE,
  getCellLogoTreatment,
  getLabTransparencyVisualState,
  getLogoSize,
  getLogoStyle,
  getTreemapCellStyle,
  type GetCellLogoTreatment,
  type GetTreemapCellStyle,
} from "./powerTreemapStyle"
import { type BlockSize } from "./useMeasuredPowerBlock"
import { type ComputeFragmentRect } from "./powerTreemapLayout"
import { type TreemapCornerLayout } from "./powerTreemapStyleCorner"

type CellBox = { left: number; top: number; width: number; height: number }

const getCellBox = (
  rect: ComputeFragmentRect,
  blockSize: BlockSize,
  gapPx: number,
): CellBox => {
  const halfGap = gapPx / 2
  const leftGap = rect.x <= 0 ? 0 : halfGap
  const topGap = rect.y <= 0 ? 0 : halfGap
  const rightGap = rect.x + rect.width >= blockSize.width - 0.01 ? 0 : halfGap
  const bottomGap = rect.y + rect.height >= blockSize.height - 0.01 ? 0 : halfGap
  return {
    left: rect.x + leftGap,
    top: rect.y + topGap,
    // Floor at 0, not 1: a cell whose layout size is below the gap only happens
    // to a cell passing through ~0 during a morph (steady-state cells are pruned
    // to ≥8px). Clamping such a cell up to 1px would make it overlap its
    // neighbour by ~1px for a frame or two; 0 keeps every frame overlap-free.
    width: Math.max(0, rect.width - leftGap - rightGap),
    height: Math.max(0, rect.height - topGap - bottomGap),
  }
}

const getCellOpacity = (
  isHoveredCell: boolean,
  labRegionId: PowerRegionId,
  hoveredRegionId: PowerRegionId | null
): number => {
  // The exact cell under the pointer sits slightly lighter than its
  // full-strength region siblings.
  if (isHoveredCell) return BLOCK_HOVERED_CELL_OPACITY
  if (hoveredRegionId === null) return 1
  return labRegionId === hoveredRegionId ? 1 : BLOCK_DIMMED_OPACITY
}

// P5: the icon fades with cell size instead of hard-toggling at the fit
// threshold, so a cell resizing past the threshold during a morph doesn't make
// its label suddenly pop in/out. Opacity ramps from 0 (a bit below the min fit
// size) to 1 (at/above it), on the cell's smaller dimension.
const ICON_FADE_RANGE_PX = 14
const iconFitOpacity = (cellBox: CellBox): number => {
  const minDim = Math.min(cellBox.width, cellBox.height)
  const t = (minDim - (LOGO_MIN_CELL_SIZE - ICON_FADE_RANGE_PX)) / ICON_FADE_RANGE_PX
  return Math.max(0, Math.min(1, t))
}

const restOfCountryIconByTailId: Partial<
  Record<string, ComponentType<IconProps>>
> = {
  "US-tail": AmericaLogo,
  "CH-tail": ChinaLogo,
  "RoW-tail": WorldLogo,
}

// Renders one lab's rectangle inside the whole-world compute treemap. Geometry
// (rect) and label opacity are supplied as EXACT per-frame values by the
// transition engine (useTreemapTransition), which morphs the layout by
// re-laying-out interpolated compute values; this component holds no animation
// of its own, so the continuous morph can't be broken by CSS-transition / mount
// quirks. Only the slow colour fade (transparency states) rides a CSS transition.
export const PowerTreemapCell = ({
  rect,
  blockSize,
  hoveredRegionId,
  hoveredLabId = null,
  topRegionId = null,
  regionRankById,
  labTransparencyLevel,
  fallbackLogoEligible = true,
  getCellStyle = getTreemapCellStyle,
  getLogoTreatment = getCellLogoTreatment,
  gapPx = BLOCK_GAP_PX,
  borderRadiusPx = BLOCK_BORDER_RADIUS_PX,
  logoSizeFraction,
  cornerLayout,
  labelOpacity = 1,
}: {
  rect: ComputeFragmentRect
  blockSize: BlockSize
  hoveredRegionId: PowerRegionId | null
  // The exact lab whose cell the pointer is on — that one cell renders at
  // BLOCK_HOVERED_CELL_OPACITY while its region siblings stay full strength.
  hoveredLabId?: PowerLabId | null
  topRegionId?: PowerRegionId | null
  regionRankById?: Record<PowerRegionId, number>
  labTransparencyLevel: number | undefined
  // The generic fallback logo is reserved for the biggest anonymous holders —
  // PowerTreemap marks only its top brand-less frontier cells eligible (see
  // getFallbackLogoLabIds); ineligible cells render plain. Cells with a real
  // brand mark are unaffected.
  fallbackLogoEligible?: boolean
  getCellStyle?: GetTreemapCellStyle
  getLogoTreatment?: GetCellLogoTreatment
  gapPx?: number
  borderRadiusPx?: number
  logoSizeFraction?: number
  cornerLayout?: TreemapCornerLayout
  // Fades an arriving cell's label in / a leaving cell's out (the box itself
  // grows/shrinks via the layout morph).
  labelOpacity?: number
}) => {
  const tuning = useAnimationTuning()
  // Only the colour fade animates via CSS; geometry & walls are exact per frame.
  const colorTransition = useMemo(
    () => {
      const e = resolveCssEasing(tuning.treemapColorEasing)
      const ms = tuning.treemapColorMs
      return `background-color ${ms}ms ${e}, border-color ${ms}ms ${e}, opacity ${ms}ms ${e}`
    },
    [tuning.treemapColorMs, tuning.treemapColorEasing],
  )

  const cellBox = getCellBox(rect, blockSize, gapPx)
  // A cell collapsed to ~0 (a leaving/entering cell mid-morph) must paint
  // nothing: even at width 0 its border would still draw a ~1.5px sliver, and a
  // cell collapsed onto a region boundary would overlap its neighbour there.
  if (cellBox.width < 0.5 || cellBox.height < 0.5) return null
  const labId = rect.fragment.lab.id as PowerLabId
  const brandIcon = labIconById[labId]
  const restCountryIcon = restOfCountryIconByTailId[rect.fragment.lab.id]
  const cellTransparencyState = getLabTransparencyVisualState(labId, labTransparencyLevel)
  const logoSize = logoSizeFraction != null
    ? Math.min(cellBox.width, cellBox.height) * logoSizeFraction
    : getLogoSize(cellBox.width, cellBox.height)
  // Frontier labs with no brand mark (anonymous "US Co N"-style holders) get
  // the generic AI-company mark — but only the biggest such holders
  // (fallbackLogoEligible, decided by PowerTreemap); the rest show nothing.
  // Only frontier cells show logos (below), so the fallback never paints on
  // border-only or "Rest of X" cells.
  const Icon = brandIcon ?? restCountryIcon ??
    (fallbackLogoEligible ? genericFrontierLabIcon : null)
  // Frontier-AI-development compute is every filled lab; the borderOnly
  // "Rest of X" residual is the non-frontier remainder. Frontier cells take
  // the red outline / red logo, and are the only cells that show a central
  // logo at all — non-frontier cells hide theirs.
  const isFrontier = rect.fragment.lab.borderOnly !== true
  // Fade the icon with cell size rather than hard-toggling it (P5).
  const fitOpacity = iconFitOpacity(cellBox)
  const showLogo = Icon != null && fitOpacity > 0.01 && isFrontier
  const cellRegionId = rect.fragment.lab.regionId
  const isHoveredCell = hoveredLabId !== null && hoveredLabId === labId
  const regionInfo = {
    regionId: cellRegionId,
    isTopRegion: topRegionId !== null && cellRegionId === topRegionId,
    regionRank: regionRankById?.[cellRegionId] ?? 0,
    isInHoveredRegion: hoveredRegionId !== null && cellRegionId === hoveredRegionId,
    isHoveredCell,
    isFrontier,
  }
  const { onWhiteBadge: logoOnWhiteBadge, color: logoColor } =
    getLogoTreatment(cellTransparencyState, regionInfo)
  const cornerSquarePx = logoSize
  const cornerIconSize = Math.max(8, logoSize - 4)
  const transparentBadgePaddingPx = 2
  const innerLogoSize = logoOnWhiteBadge
    ? Math.max(8, logoSize - transparentBadgePaddingPx * 2)
    : logoSize
  const logoStyle = getLogoStyle(cellTransparencyState)

  const iconContent = !showLogo || !Icon ? null : cornerLayout ? (
    <span
      style={{
        position: "absolute", top: 0, left: 0,
        width: cornerSquarePx, height: cornerSquarePx,
        display: "flex", alignItems: "center", justifyContent: "center",
        backgroundColor: cornerLayout.iconBackground,
        borderRadius: borderRadiusPx,
        ...logoStyle,
      }}
    >
      <Icon size={cornerIconSize} color={cornerLayout.iconColor} />
    </span>
  ) : logoOnWhiteBadge ? (
    <span
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        backgroundColor: "#ffffff",
        borderRadius: Math.max(borderRadiusPx, 4),
        padding: transparentBadgePaddingPx, boxSizing: "border-box",
        ...logoStyle,
      }}
    >
      <Icon size={innerLogoSize} color={logoColor} />
    </span>
  ) : (
    <Icon size={logoSize} color={logoColor} style={logoStyle} />
  )

  const labelLayer = (
    <span
      style={{
        position: "absolute", inset: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        opacity: labelOpacity * fitOpacity,
      }}
    >
      {iconContent}
    </span>
  )

  const cellStyle: CSSProperties = {
    ...getCellStyle({
      ...cellBox,
      opacity: getCellOpacity(isHoveredCell, cellRegionId, hoveredRegionId),
      state: cellTransparencyState,
      ...regionInfo,
      isHoveredCell,
    }),
    transition: colorTransition,
  }

  return (
    // Hover is resolved by PowerTreemap's block-level mousemove hit test (not
    // a per-cell mouseenter), so the cell renders hover state but doesn't
    // report it.
    <div
      className="absolute flex items-center justify-center overflow-hidden"
      aria-label={rect.fragment.lab.name}
      data-cell-id={rect.fragment.id}
      style={cellStyle}
    >
      {labelLayer}
    </div>
  )
}
